import clsx from 'clsx';
import { CalendarPlus } from 'lucide-react';
import { useState } from 'react';
import { studioApi, useCancelMeeting, useEditMeeting, useFirefliesDraft, usePlanMeeting, useRecordOutcome, useStudioMeetings } from '@/api/studio';
import type { Meeting, MeetingFreq, MeetingScope, Project } from '@/api/types';
import { CalendarSubscribe } from '@/components/CalendarSubscribe';
import { DownloadButton } from '@/components/DownloadButton';
import { RowsInput } from '@/components/RowsInput';
import { filled } from '@/lib/rows';
import { QueryPending } from '@/components/QueryState';
import { Status } from '@/components/ui';
import { formatDate, formatTime } from '@/lib/format';
import { icsName, isCanceled, isHeld, recurrenceLabel, splitMeetings } from '@/lib/meetings';

const DURATIONS = [15, 30, 45, 60, 90, 120, 180, 240];
const REPEAT: [MeetingFreq | 'none', string][] = [
  ['none', 'Не повторять'],
  ['weekly', 'Каждую неделю'],
  ['biweekly', 'Раз в две недели'],
  ['monthly', 'Раз в месяц'],
];

/** ISO (UTC) → the value of a datetime-local input, in the browser's time zone. */
const toLocal = (iso: string) => {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const errorText = (e: unknown) => (e instanceof Error ? e.message : 'Не получилось, попробуйте ещё раз');

function PlanForm({ p, editing, onDone }: { p: Project; editing: Meeting | null; onDone: () => void }) {
  const plan = usePlanMeeting();
  const edit = useEditMeeting();
  const [title, setTitle] = useState(editing?.title ?? '');
  const [at, setAt] = useState(editing ? toLocal(editing.at) : '');
  const [duration, setDuration] = useState(String(editing?.durationMin ?? 60));
  const [link, setLink] = useState(editing?.link ?? '');
  const [repeat, setRepeat] = useState<MeetingFreq | 'none'>('none');
  const [until, setUntil] = useState('');
  const [agenda, setAgenda] = useState<string[]>(editing?.agenda.length ? editing.agenda : ['']);
  const [scope, setScope] = useState<MeetingScope>('one');
  const pending = plan.isPending || edit.isPending;
  const error = plan.error ?? edit.error;
  const durations = DURATIONS.includes(Number(duration)) ? DURATIONS : [...DURATIONS, Number(duration)].sort((a, b) => a - b);

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!title.trim() || !at) return;
        const fields = { title: title.trim(), at: new Date(at).toISOString(), durationMin: Number(duration), agenda: filled(agenda) };
        if (editing) edit.mutate({ id: editing.id, patch: { ...fields, link: link.trim() || null }, scope }, { onSuccess: onDone });
        else plan.mutate({ projectId: p.id, ...fields, link: link.trim() || undefined, ...(repeat !== 'none' ? { recurrence: { freq: repeat, until } } : {}) }, { onSuccess: onDone });
      }}
    >
      <input className="field" aria-label="Тема встречи" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Тема встречи" />
      <div className="grid grid-cols-[minmax(0,1fr)_110px] gap-2">
        <input className="field" aria-label="Дата и время" type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} />
        <select className="field" aria-label="Длительность" value={duration} onChange={(e) => setDuration(e.target.value)}>
          {durations.map((d) => (
            <option key={d} value={d}>
              {d} мин
            </option>
          ))}
        </select>
      </div>
      <input className="field" aria-label="Ссылка Zoom" type="url" value={link} onChange={(e) => setLink(e.target.value)} placeholder="Ссылка Zoom (https://…)" />
      {editing ? (
        editing.seriesId && (
          <div className="flex flex-wrap gap-4 text-sm" role="radiogroup" aria-label="Что изменить">
            <label className="flex items-center gap-1.5">
              <input type="radio" name="scope" checked={scope === 'one'} onChange={() => setScope('one')} /> Только эту
            </label>
            <label className="flex items-center gap-1.5">
              <input type="radio" name="scope" checked={scope === 'following'} onChange={() => setScope('following')} /> Эту и следующие
            </label>
          </div>
        )
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[200px_1fr]">
          <select className="field" aria-label="Повтор" value={repeat} onChange={(e) => setRepeat(e.target.value as MeetingFreq | 'none')}>
            {REPEAT.map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
          {repeat !== 'none' && (
            <label className="flex items-center gap-2 text-sm text-muted">
              до <input className="field w-44" type="date" aria-label="Повторять до" value={until} onChange={(e) => setUntil(e.target.value)} />
              <span className="text-xs">не больше 26 встреч</span>
            </label>
          )}
        </div>
      )}
      <RowsInput label="Повестка" values={agenda} onChange={setAgenda} placeholder="Пункт повестки" addLabel="Добавить пункт" />
      {error && (
        <p role="alert" className="text-sm text-overdue">
          {errorText(error)}
        </p>
      )}
      <div className="flex gap-2">
        <button className="btn-primary" disabled={!title.trim() || !at || (!editing && repeat !== 'none' && !until) || pending}>
          {editing ? 'Сохранить' : 'Запланировать'}
        </button>
        {editing && (
          <button type="button" className="btn-ghost" onClick={onDone}>
            Отмена
          </button>
        )}
      </div>
    </form>
  );
}

function Outcome({ m }: { m: Meeting }) {
  const record = useRecordOutcome();
  const draft = useFirefliesDraft();
  const [summary, setSummary] = useState('');
  const [decisions, setDecisions] = useState<string[]>(['']);
  const [transcript, setTranscript] = useState('');
  const fill = () =>
    draft.mutate(m.id, {
      onSuccess: (d) => {
        setSummary(d.summary);
        setDecisions(d.decisions.length ? d.decisions : ['']);
        setTranscript(d.transcriptUrl);
      },
    });
  return (
    <div className="mt-3 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className="btn-secondary h-8 px-3 text-sm" disabled={draft.isPending} onClick={fill}>
          {draft.isPending ? 'Ищем запись…' : 'Подтянуть из Fireflies'}
        </button>
        <span className="text-xs text-muted">Черновик из записи встречи: проверьте его перед публикацией.</span>
      </div>
      {draft.error && (
        <p role="alert" className="text-sm text-overdue">
          {errorText(draft.error)}
        </p>
      )}
      <input className="field" aria-label="Итог" placeholder="Итог в одну фразу" value={summary} onChange={(e) => setSummary(e.target.value)} />
      <RowsInput label="Решения" values={decisions} onChange={setDecisions} placeholder="Что решили" />
      <input className="field" aria-label="Ссылка на расшифровку" type="url" placeholder="Ссылка на расшифровку (видна только студии)" value={transcript} onChange={(e) => setTranscript(e.target.value)} />
      <button className="btn-primary" disabled={!summary.trim() || record.isPending} onClick={() => record.mutate({ id: m.id, outcome: { summary: summary.trim(), decisions: filled(decisions), ...(transcript.trim() ? { transcriptUrl: transcript.trim() } : {}) } })}>
        Опубликовать итоги
      </button>
    </div>
  );
}

function Row({ m, onEdit }: { m: Meeting; onEdit: (m: Meeting) => void }) {
  const cancel = useCancelMeeting();
  const [asking, setAsking] = useState(false);
  const canceled = isCanceled(m);
  const past = new Date(m.at).getTime() < Date.now();
  const series = recurrenceLabel(m);
  const ics = 'btn-ghost h-7 gap-1 px-2 text-xs';
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
        <span className={clsx('font-medium', canceled && 'text-muted line-through')}>{m.title}</span>
        <span className="text-muted">
          {formatDate(m.at)}, {formatTime(m.at)}
          {m.durationMin ? `, ${m.durationMin} мин` : ''}
        </span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted">
        {canceled && <Status tone="neutral">Отменена</Status>}
        {isHeld(m) && <Status tone="done">Прошла</Status>}
        {series && <span>Серия: {series}</span>}
        {m.link && !canceled && (
          <a href={m.link} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-ink">
            Ссылка
          </a>
        )}
      </div>
      {m.outcome && (
        <div className="mt-1 text-sm text-muted">
          <p>Итоги: {m.outcome.summary}</p>
          {m.transcriptUrl && (
            <a href={m.transcriptUrl} target="_blank" rel="noreferrer" className="text-xs underline underline-offset-2 hover:text-ink">
              Расшифровка
            </a>
          )}
        </div>
      )}
      <div className="mt-1.5 flex flex-wrap items-center gap-1">
        <DownloadButton className={ics} name={icsName(m)} label="В календарь (.ics)" load={() => studioApi.meetingIcs(m.id)}>
          <CalendarPlus className="size-3.5" /> В календарь (.ics)
        </DownloadButton>
        {m.seriesId && (
          <DownloadButton className={ics} name={icsName(m, true)} label="Вся серия (.ics)" load={() => studioApi.meetingIcs(m.id, true)}>
            Вся серия (.ics)
          </DownloadButton>
        )}
        {!canceled && !past && (
          <>
            <button className="btn-ghost h-7 px-2 text-xs" onClick={() => onEdit(m)}>
              Изменить
            </button>
            {!asking && (
              <button className="btn-ghost h-7 px-2 text-xs" onClick={() => (m.seriesId ? setAsking(true) : cancel.mutate({ id: m.id }))}>
                Отменить
              </button>
            )}
          </>
        )}
      </div>
      {asking && (
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm">
          Отменить:
          <button className="btn-secondary h-7 px-2.5 text-xs" disabled={cancel.isPending} onClick={() => cancel.mutate({ id: m.id, scope: 'one' }, { onSuccess: () => setAsking(false) })}>
            Только эту
          </button>
          <button className="btn-secondary h-7 px-2.5 text-xs" disabled={cancel.isPending} onClick={() => cancel.mutate({ id: m.id, scope: 'following' }, { onSuccess: () => setAsking(false) })}>
            Эту и следующие
          </button>
          <button className="btn-ghost h-7 px-2 text-xs" onClick={() => setAsking(false)}>
            Назад
          </button>
        </div>
      )}
      {past && !canceled && !m.outcome && <Outcome m={m} />}
    </li>
  );
}

/** Plan meetings (once or as a series), edit and cancel them, write the outcome of the held ones. */
export function StudioMeetings({ p }: { p: Project }) {
  const q = useStudioMeetings(p.id);
  const meetings = q.data;
  const [editing, setEditing] = useState<Meeting | null>(null);
  // A new key is a blank form: after planning, and when switching between meetings being edited.
  const [round, setRound] = useState(0);
  const { upcoming, past } = splitMeetings(meetings ?? []);

  return (
    <div className="grid grid-cols-1 gap-12 lg:grid-cols-[minmax(0,420px)_1fr]">
      <section>
        <h2 className="mb-3 text-sm font-semibold">{editing ? `Изменить: ${editing.title}` : 'Запланировать встречу'}</h2>
        <PlanForm key={`${editing?.id ?? 'new'}-${round}`} p={p} editing={editing} onDone={() => (setEditing(null), setRound((n) => n + 1))} />
        <p className="mt-3 text-xs text-muted">Ссылку Zoom создаёте сами и вставляете сюда: клиент увидит кнопку «Подключиться» и получит файл для календаря.</p>
      </section>
      <section className="min-w-0">
        {!meetings ? (
          <QueryPending query={q} />
        ) : (
          <>
            <div className="mb-3 flex justify-end">
              <CalendarSubscribe />
            </div>
            <h2 className="mb-1 text-sm font-semibold">Предстоящие</h2>
            {upcoming.length ? (
              <ul className="divide-y divide-line border-y border-line">
                {upcoming.map((m) => (
                  <Row key={m.id} m={m} onEdit={setEditing} />
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted">Не запланированы.</p>
            )}
            <h2 className="mb-1 mt-8 text-sm font-semibold">Прошедшие</h2>
            {past.length ? (
              <ul className="divide-y divide-line border-y border-line">
                {past.map((m) => (
                  <Row key={m.id} m={m} onEdit={setEditing} />
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted">Пока нет.</p>
            )}
          </>
        )}
      </section>
    </div>
  );
}
